// provisional-material.ts — provisional material created during a quote.
//
// When the material search finds nothing, sales can create a provisional material: a subset of the
// full checklist (identity, tier 1 PA, minima), a ref from the real series suffixed "-NEW" (reserved
// atomically in the database, never recycled), and a `provisional` status distinct from inactive: an
// inactive material exists but appears nowhere, whereas a provisional one is usable in its
// originating quote only. It does not live in `catalog_items`, so global menus and catalogue
// exports never see it.
//
// Pricing: bare PV €/m² = tier 1 total PA / (1 − tier 1 target margin), both read from the injected
// grid. As intended, recomputing the target PA at tier 1 lands exactly on the entered PA. Later
// tiers use the standard target-PA mechanism, since a -NEW ref has no collected PA grid and the
// lookup lock prevents a workshop's generic key from catching it; the workshop is only a
// preselection. The usual cascade follows: agency, tier, general discount and minima floors.
//
// Deferred to the full supervisor-side creation: weight, formats, compatibilities, CB- combinations,
// UPZ twin and CRM ref. Meanwhile standardPrint is empty, kg is 0, and no family restricts the menus.

import type { Material, PaSupplier } from "../../models/data.model";
import { SERVICE_KEY, CATALOGUE, PA_SUPPLIERS } from "../../data";
import type { Catalogue } from "./catalogue-source";
import type { VolumeGrid } from "../pricing/volume-tiers";
import { type QuoteLine, num, round2 } from "../core/core";
import { isLargeFormatPrintRegime } from "../core/regimes";
import { nextRef, M2_RANGE } from "./reference-creation";

/** Suffix of provisional refs. The final ref is the same root without the suffix. */
export const PROVISIONAL_SUFFIX = "-NEW";

/**
 * Is a ref provisional (AD-1042-NEW)?
 * @param ref The reference.
 * @returns True when the ref carries the provisional suffix.
 */
export function isProvisionalRef(ref: string | null | undefined): boolean {
  return typeof ref === "string" && ref.endsWith(PROVISIONAL_SUFFIX);
}

/**
 * Final ref of a provisional one: same root, suffix removed (AD-1042-NEW to AD-1042).
 * @param ref The reference.
 * @returns The final ref, or the ref itself when it is not provisional.
 */
export function finalRef(ref: string): string {
  return isProvisionalRef(ref) ? ref.slice(0, -PROVISIONAL_SUFFIX.length) : ref;
}

/**
 * Provisional material: the panel's fields, a subset of the full checklist.
 * `workshopPaM2` is the workshop's print+cut PA in the UPZ case, else the full tier 1 workshop PA.
 * `materialPaM2` and `materialSupplier` apply to the UPZ case only.
 */
export interface ProvisionalMaterial {
  ref: string;
  itemShort: string;
  /** Existing category (closed list; a new category stays a supervisor decision). */
  category: string;
  isUpz: boolean;
  /** Attached workshop: a line preselection, never a declared production capacity. */
  workshop: string;
  workshopPaM2: number;
  materialPaM2: number | null;
  materialSupplier: string;
  minUnitPrice: number;
  minFlatFee: number;
  /** Sellsy CRM ref, deferred to the full creation; a fallback entry is possible. */
  refCrm: string | null;
  status: "provisional" | "active";
}

/**
 * Tier 1 total PA (€/m²): workshop work plus supplier material in the UPZ case.
 * @param p The provisional material (its PA fields).
 * @returns The tier 1 total PA.
 */
export function paTotalP1(p: Pick<ProvisionalMaterial, "isUpz" | "workshopPaM2" | "materialPaM2">): number {
  return num(p.workshopPaM2) + (p.isUpz ? num(p.materialPaM2) : 0);
}

/**
 * Bare PV €/m² of a provisional material: tier 1 total PA / (1 − tier 1 target margin), the margin
 * being read from the injected grid. Tier 1 is the grid's first tier.
 * @param paTotal The tier 1 total PA.
 * @param grid The injected volume grid.
 * @returns The bare PV €/m².
 */
export function provisionalBarePv(paTotal: number, grid: VolumeGrid): number {
  const p1 = grid.tiers[0];
  if (!p1) throw new Error("provisionalBarePv : grille margin_tiers vide");
  const margin = p1.targetMargin;
  if (!(margin >= 0 && margin < 1)) throw new Error(`provisionalBarePv : marge cible P1 invalide (${margin})`);
  return round2(num(paTotal) / (1 - margin));
}

/**
 * Converts to a `Material` injectable into the originating quote's calculation catalogue. It is
 * priced like a standard material without a PA grid, so no `stockUpz`: its UPZ nature is collection
 * metadata, not a calculation regime.
 * @param p The provisional material.
 * @param grid The injected volume grid.
 * @returns The material, priced from its tier 1 PA.
 */
export function toCatalogueMaterial(p: ProvisionalMaterial, grid: VolumeGrid): Material {
  return {
    ref: p.ref,
    name: p.itemShort,
    fullName: p.itemShort,
    category: p.category,
    pv: provisionalBarePv(paTotalP1(p), grid),
    min_flat_fee: num(p.minFlatFee),
    min_unit_price: num(p.minUnitPrice),
    standardPrint: "",
    kg: 0,
    isMoq: false,
  };
}

/**
 * Calculation catalogue of the originating quote: the current catalogue plus its provisional
 * materials still in `provisional` status. Every other quote gets the bare catalogue.
 * @param base The current catalogue.
 * @param provisionals The provisional materials of the quote.
 * @param grid The injected volume grid.
 * @returns The catalogue to calculate the originating quote with.
 */
export function catalogueWithProvisionals(
  base: Catalogue,
  provisionals: readonly ProvisionalMaterial[],
  grid: VolumeGrid,
): Catalogue {
  const live = provisionals.filter(
    (p) => p.status === "provisional" && !base.materials.some((m) => m.ref === p.ref),
  );
  if (!live.length) return base;
  return { ...base, materials: [...base.materials, ...live.map((p) => toCatalogueMaterial(p, grid))] };
}

// Expected provisional ref (indicative; the database reservation is authoritative)

/**
 * Series prefix of a category, derived from the category's existing refs rather than a list.
 * @param category The category.
 * @param materials The catalogue materials.
 * @returns The prefix, or null when the category has no series.
 */
export function prefixFromCategory(category: string, materials: readonly Material[] = CATALOGUE): string | null {
  const cat = (category ?? "").trim();
  if (!cat) return null;
  for (const m of materials) {
    if (m.category === cat) {
      const p = /^([A-Za-z]+)-/.exec(m.ref);
      if (p) return p[1]!.toUpperCase();
    }
  }
  return null;
}

/**
 * Expected provisional ref for a category: series prefix, next sequential number in range, and the
 * -NEW suffix. The refs passed in cover the catalogue and already-reserved provisional ones, whose
 * suffixes are stripped so they still occupy their number. Null when the category has no series.
 * @param category The category.
 * @param existingRefs The catalogue refs and the already-reserved provisional refs.
 * @param materials The catalogue materials.
 * @returns The expected provisional ref, or null.
 */
export function expectedProvisionalRef(
  category: string,
  existingRefs: readonly string[],
  materials: readonly Material[] = CATALOGUE,
): string | null {
  const pfx = prefixFromCategory(category, materials);
  return pfx ? expectedRefForPrefix(pfx, existingRefs) : null;
}

/**
 * Expected provisional ref of a series prefix typed by hand (a new category has no series yet): next number of
 * the series in range, -NEW suffix. Null when the prefix is not letters only (the database refuses it too), or when
 * the material settings do not know the series: its material would have no nature and block the quote.
 * @param prefix The series prefix (e.g. `AD`), any case.
 * @param existingRefs The catalogue refs and the already-reserved provisional refs.
 * @param knownSeries The series of the material settings (all accepted when omitted).
 * @returns The expected provisional ref, or null.
 */
export function expectedRefForPrefix(
  prefix: string,
  existingRefs: readonly string[],
  knownSeries?: readonly string[],
): string | null {
  const pfx = (prefix ?? "").trim().toUpperCase();
  if (!/^[A-Z]+$/.test(pfx)) return null;
  if (knownSeries && !knownSeries.some((s) => s.toUpperCase() === pfx)) return null;
  const roots = existingRefs.map((r) => finalRef(r));
  return nextRef(pfx, roots, M2_RANGE) + PROVISIONAL_SUFFIX;
}

// Pre-filling the print+cut PA in the UPZ case, from the nearest case in the workshop grid

/**
 * Default print+cut PA of a workshop: the first generic key (empty material) of its SUPPLIED (or
 * ALL) grid, the closest honest case for a material the workshop does not know yet. Returns tier 1
 * and the key used, or null when the grid has no generic key. Input assistance only; the line's
 * pricing still uses the target PA.
 * @param workshop The workshop name.
 * @param paGrids The PA tables.
 * @returns Tier 1 PA and the key used, or null.
 */
export function defaultWorkshopPa(
  workshop: string,
  paGrids: readonly PaSupplier[] = PA_SUPPLIERS,
): { paM2: number; print: string; cut: string } | null {
  const K = SERVICE_KEY[workshop];
  if (!K) return null;
  const e = paGrids.find((x) => x.workshop === K && x.material === "" && (x.mode === "SUPPLIED" || x.mode === "ALL"));
  if (!e) return null;
  return { paM2: e.pa[0] ?? 0, print: e.print, cut: e.cut };
}

// Panel validation: a single gate, in the same spirit as reference-creation

/** Panel draft: text fields are tolerated, the validator normalizes them. */
export interface ProvisionalDraft {
  itemShort: string;
  category: string;
  isUpz: boolean;
  workshop: string;
  workshopPaM2: number | string | null;
  materialPaM2: number | string | null;
  materialSupplier: string;
  minUnitPrice: number | string | null;
  minFlatFee: number | string | null;
}

/** Validation context of the panel. */
export interface ProvisionalContext {
  /** Existing categories (derived from the current catalogue): a typed category takes their spelling. */
  existingCategories: readonly string[];
}

/** Result of the panel validation. */
export interface ProvisionalResult {
  /** Blocking reasons (French, shown to the user). */
  blockers: string[];
  creatable: boolean;
  /** Normalized fields, without the ref (assigned by the atomic reservation) or the status. */
  normalized: Omit<ProvisionalMaterial, "ref" | "refCrm" | "status"> | null;
}

/**
 * Is the draft value a finite number (not blank)?
 * @param v The draft value.
 * @returns True when it is a number.
 */
const isNumber = (v: number | string | null | undefined): boolean =>
  v != null && String(v).trim() !== "" && Number.isFinite(num(v));

/**
 * Validates the "create a provisional material" panel. Every missing field blocks, since the panel
 * is already a subset. The category is typed by hand: an existing one keeps its catalogue spelling, a new one is
 * taken as typed.
 * @param b The panel draft.
 * @param ctx The validation context.
 * @returns The blockers and, when creatable, the normalized fields.
 */
export function validateProvisionalMaterial(b: ProvisionalDraft, ctx: ProvisionalContext): ProvisionalResult {
  const blockers: string[] = [];
  const itemShort = (b.itemShort ?? "").trim();
  const typed = (b.category ?? "").trim();
  const category = ctx.existingCategories.find((c) => c.toLowerCase() === typed.toLowerCase()) ?? typed;
  const workshop = (b.workshop ?? "").trim();
  const supplier = (b.materialSupplier ?? "").trim();

  if (!itemShort) blockers.push("engineProvItemShortRequired");
  if (!category) blockers.push("engineProvCategoryRequired");
  if (!workshop) blockers.push("engineProvWorkshopRequired");
  if (!isNumber(b.workshopPaM2) || num(b.workshopPaM2) <= 0)
    blockers.push(b.isUpz ? "engineProvWorkshopPaUpzRequired" : "engineProvWorkshopPaRequired");
  if (b.isUpz) {
    if (!isNumber(b.materialPaM2) || num(b.materialPaM2) <= 0)
      blockers.push("engineProvMaterialPaRequired");
    if (!supplier) blockers.push("engineProvSupplierRequired");
  }
  if (!isNumber(b.minUnitPrice) || num(b.minUnitPrice) < 0) blockers.push("engineProvMinUnitPriceRequired");
  if (!isNumber(b.minFlatFee) || num(b.minFlatFee) < 0) blockers.push("engineProvFlatFeeRequired");

  if (blockers.length) return { blockers, creatable: false, normalized: null };
  return {
    blockers: [],
    creatable: true,
    normalized: {
      itemShort,
      category,
      isUpz: !!b.isUpz,
      workshop,
      workshopPaM2: num(b.workshopPaM2),
      materialPaM2: b.isUpz ? num(b.materialPaM2) : null,
      materialSupplier: b.isUpz ? supplier : "",
      minUnitPrice: num(b.minUnitPrice),
      minFlatFee: num(b.minFlatFee),
    },
  };
}

// Sellsy send sequence: every line carries a catalogue ref, never a free-text line

/**
 * Operations of the send sequence, strictly data with no network call: the executor plays them in
 * order and stops if a CRM ref creation fails, which blocks the push.
 */
export type SellsyOperation =
  | { type: "create_ref_crm"; ref: string; itemShort: string }
  | { type: "push_quote" };

/** The strict send sequence of a quote carrying provisional materials. */
export interface SellsyDispatchSequence {
  /** CRM ref creations first, push after; the push is absent while any ref is blocked. */
  operations: SellsyOperation[];
  /** Dead-end -NEW refs, unknown among the quote's provisional materials: the push is impossible. */
  blockedRefs: string[];
  /** True when the push can go out, after the listed creations, in order. */
  pushPossible: boolean;
}

/**
 * -NEW refs actually carried by the quote's print lines, deduplicated in order of appearance.
 * @param lines The quote lines.
 * @returns The provisional refs.
 */
export function provisionalRefsOnQuote(lines: readonly QuoteLine[]): string[] {
  const seen = new Set<string>();
  for (const l of lines) {
    if (isLargeFormatPrintRegime(l) && isProvisionalRef(l.material) && !seen.has(l.material!)) seen.add(l.material!);
  }
  return [...seen];
}

/**
 * Strict post-validation send sequence: one CRM ref creation per provisional material that lacks
 * one, then the quote push. A material that already carries its CRM ref triggers no creation, and a
 * -NEW ref unknown among the quote's provisional materials blocks the push.
 * @param lines The quote lines.
 * @param provisionals The quote's provisional materials.
 * @returns The ordered operations, the blocked refs and whether the push is possible.
 */
export function sellsyDispatchSequence(
  lines: readonly QuoteLine[],
  provisionals: readonly ProvisionalMaterial[],
): SellsyDispatchSequence {
  const refs = provisionalRefsOnQuote(lines);
  const operations: SellsyOperation[] = [];
  const blockedRefs: string[] = [];
  for (const ref of refs) {
    const p = provisionals.find((x) => x.ref === ref);
    if (!p) blockedRefs.push(ref);
    else if (!(p.refCrm ?? "").trim()) operations.push({ type: "create_ref_crm", ref, itemShort: p.itemShort });
  }
  const pushPossible = blockedRefs.length === 0;
  if (pushPossible) operations.push({ type: "push_quote" });
  return { operations, blockedRefs, pushPossible };
}
